import { Box, Flex, HStack, Icon, Tooltip } from '@chakra-ui/react';
import { TbAlertTriangle, TbRefresh } from 'react-icons/tb';
import type { IconType } from 'react-icons';
import type { SelectionChangeIndicator, SelectionPart } from '../../hooks/useGetOrderDetail';

const ACTION_ICON_SIZE_PX = 32;
const ACTION_ICON_GAP_PX = 8;
const BASE_ACTION_SLOTS = 2;

export interface SelectionActionVisibility {
    hasChat: boolean;
    hasMenu: boolean;
}

type SelectionSectionKey = 'all' | 'pending' | 'excluded' | 'no-quoted' | 'ordered';

export function getSelectionTabsActionVisibility(
    part: SelectionPart,
    sectionKey: SelectionSectionKey,
    isEditable: boolean,
): SelectionActionVisibility {
    const hasChat = !!(part.notes?.length || sectionKey === 'pending') && isEditable;
    const hasMenu =
        sectionKey !== 'ordered' &&
        !(
            sectionKey === 'all' &&
            !part.excludeOrdering &&
            part.status !== 'selection' &&
            part.status !== 'selection-excluded-temporary'
        );

    return { hasChat, hasMenu };
}

export function getMaxIndicatorSlotCount(parts: Array<{ changeIndicators?: Array<SelectionChangeIndicator> }>): number {
    if (parts.length === 0) return 0;
    return Math.max(...parts.map((part) => part.changeIndicators?.length ?? 0));
}

export function getActionColumnMinWidth(totalSlotCount: number): string {
    const count = Math.max(BASE_ACTION_SLOTS, totalSlotCount);
    return `${count * ACTION_ICON_SIZE_PX + (count - 1) * ACTION_ICON_GAP_PX}px`;
}

export function getActionColumnSlotCount(parts: Array<{ changeIndicators?: Array<SelectionChangeIndicator> }>): number {
    return BASE_ACTION_SLOTS + getMaxIndicatorSlotCount(parts);
}

interface IndicatorConfig {
    icon: IconType;
    label: string;
    color: string;
    bg: string;
}

const INDICATOR_CONFIG: Record<SelectionChangeIndicator, IndicatorConfig> = {
    'rejected-cancelled': {
        icon: TbAlertTriangle,
        label: 'Part was previously rejected or cancelled',
        color: 'red.500',
        bg: 'red.50',
    },
    returned: {
        icon: TbRefresh,
        label: 'Part was previously returned',
        color: 'blue.500',
        bg: 'blue.50',
    },
};

const INDICATOR_ORDER: Array<SelectionChangeIndicator> = ['rejected-cancelled', 'returned'];

interface SelectionChangeIndicatorsProps {
    indicators?: Array<SelectionChangeIndicator>;
    reservedIndicatorSlots?: number;
}

const ACTION_SLOT_BOX_PROPS = {
    flexShrink: 0,
    w: 8,
    h: 8,
} as const;

const SelectionChangeIndicators = ({ indicators = [], reservedIndicatorSlots = 0 }: SelectionChangeIndicatorsProps) => {
    if (reservedIndicatorSlots === 0) return null;

    const visibleIndicators = INDICATOR_ORDER.filter((indicator) => indicators.includes(indicator));

    const renderIndicatorBadge = (indicator: SelectionChangeIndicator) => {
        const config = INDICATOR_CONFIG[indicator];

        return (
            <Tooltip key={indicator} label={config.label} hasArrow placement="top">
                <Flex
                    role="img"
                    aria-label={config.label}
                    align="center"
                    justify="center"
                    borderRadius="md"
                    color={config.color}
                    bg={config.bg}
                    {...ACTION_SLOT_BOX_PROPS}
                >
                    <Icon as={config.icon as React.ElementType} w={4} h={4} />
                </Flex>
            </Tooltip>
        );
    };

    if (reservedIndicatorSlots === 2) {
        return (
            <HStack spacing={1}>
                {INDICATOR_ORDER.map((indicator) => (
                    indicators.includes(indicator)
                        ? renderIndicatorBadge(indicator)
                        : <Box key={indicator} {...ACTION_SLOT_BOX_PROPS} />
                ))}
            </HStack>
        );
    }

    const singleIndicator = visibleIndicators[0];

    return (
        <Box {...ACTION_SLOT_BOX_PROPS}>
            {singleIndicator ? renderIndicatorBadge(singleIndicator) : null}
        </Box>
    );
};

export default SelectionChangeIndicators;
